import React from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getUrlForStaticApi } from "../../../../utils/urlStaticFiles";
import downloadFile from "../../../../utils/downloadFile";
import CloudDownloadIcon from "../../../../icons/CloudDownloadIcon";
import moment from "moment";
import { showError } from "../../../../utils/notifications";

const DocumentItem = ({ title, type, Icon, documents: initDocuments }) => {
  const classes = useStyles();
  const documents = (initDocuments || []).filter((t) => t.type === type);

  return (
    <Box className={classes.root}>
      <div className={classes.title}>
        {Icon && <Icon sx={{ fontSize: 18, color: "#4F39F6" }} />}
        {title}
      </div>
      {documents.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {documents.map((doc, index) => (
            <DocumentItemRow key={doc.id || index} document={doc} />
          ))}
        </Box>
      ) : (
        <Box sx={{ fontSize: 14, color: "#71717A", py: 2, textAlign: "center" }}>Нет документов</Box>
      )}
    </Box>
  )
}

const DocumentItemRow = ({ document }) => {
  const classes = useStyles();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const printSize = React.useMemo(() => {
    const bytes = document.size;
    if (!bytes || bytes === 0) return "0 Б";
    const k = 1024;
    const sizes = ["Б", "КБ", "МБ", "ГБ"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
  }, [document.size]);

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!document.url || isDownloading) return;
    setIsDownloading(true);
    try {
      const ok = await downloadFile(getUrlForStaticApi(document.url), document.original_name, {
        allowAnchorFallback: false,
      });
      if (!ok) {
        showError("Ошибка скачивания", "Не удалось скачать файл. Проверьте доступ к хранилищу.");
      }
    } catch {
      showError("Ошибка скачивания", "Не удалось скачать файл");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={classes.documentRow}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip arrow title={document.original_name}>
          <div className={classes.docName}>{document.original_name}</div>
        </Tooltip>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <span className={classes.docMeta}>{printSize}</span>
          <span className={classes.docMeta}>{moment(document.created).format('DD.MM.YYYY')}</span>
          <Chip label={document.format} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 500, backgroundColor: "#F4F4F5", color: "#71717A" }} />
        </Box>
      </Box>
      <Tooltip title="Скачать" arrow>
        <span>
          <IconButton
            size="small"
            sx={{ color: "#4F39F6", flexShrink: 0 }}
            disabled={!document.url || isDownloading}
            onClick={handleDownload}
          >
            <CloudDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  root: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FAFAFA",
    border: "1px solid #E4E4E7",
  },
  title: {
    gap: 6,
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    fontSize: 14,
    color: "#27272A",
    fontWeight: 600,
    lineHeight: "20px",
  },
  documentRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    border: "1px solid #E4E4E7",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0px 2px 8px -2px rgba(22, 34, 51, 0.08)",
    },
  },
  docName: {
    fontSize: 14,
    color: "#27272A",
    fontWeight: 500,
    lineHeight: "18px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  docMeta: {
    fontSize: 12,
    color: "#71717A",
    lineHeight: "16px",
  },
}));

export default DocumentItem;
